import React, { useEffect, useRef, useState } from 'react';
import { Message, Modal } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { gisModalActions } from '../../behavior/gisModal';
import ButtonWrapper from '../common/ButtonWrapper';
import { Loading } from 'react-lib/dist/components-es';
import { VIEW_MODE } from '../../constants';

type GisModalTriggerProps = {
  onClick: () => void;
  label: string;
};

const GisModalTrigger = ({ onClick, label }: GisModalTriggerProps) => (
  <ButtonWrapper type="button" label={label} primary={true} onClick={onClick} icon="map outline" />
);

type Props = {
  toggleGisModal?: (arg: boolean) => void;
  open?: boolean;
  selectedInventory: {
    location?: { locationInfo: any };
    id?: number;
  };
  mode?: string;
  onItemEdited?: (editType: string, feature: any, geometry: any) => void;
};

const GisModal: React.FC<Props> = ({
  toggleGisModal = () => {},
  open = false,
  selectedInventory = {},
  mode = VIEW_MODE,
  onItemEdited = () => {},
}) => {
  const { t } = useTranslation();
  const mapRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const getMapConfig = () => ({
    location: selectedInventory.location?.locationInfo ?? null,
    layer: window.REACT_APP_GIS_MAP_RISKS_LAYER,
    itemId: selectedInventory.id,
    onItemEdited,
    onClick: clickCallback,
    onError: errorCallback,
    onMapLoaded: mapLoadedCallback,
    sso: 'Keycloak',
    ...(mode !== VIEW_MODE && { editLayer: window.REACT_APP_GIS_MAP_RISKS_LAYER }),
  });

  const clickCallback = (geometry: any) => {
    console.log(geometry);
  };

  const mapLoadedCallback = () => {
    setIsLoaded(true);
  };

  const errorCallback = (error: any) => {
    toast.error(t('message.title.error.gis.callback') + error);
    throw new Error('An Error occurred on GIS side: ' + error);
  };

  useEffect(() => {
    if (open && !mapRef.current) {
      mapRef.current = (window as any).CERNMap
        ? new (window as any).CERNMap(
            document.getElementById('map'),
            window.REACT_APP_GIS_MAP_URL,
            getMapConfig()
          )
        : null;
    } else if (mapRef.current && open) {
      mapRef.current.config = getMapConfig();
      mapRef.current.display(mapRef.current.config);
    } else {
      setIsLoaded(false);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, [open]);

  return (
    <Modal
      trigger={
        <GisModalTrigger
          onClick={() => toggleGisModal(true)}
          label={t('gis.modal.precise.location.trigger.label')}
        />
      }
      centered
      open={open}
      onClose={() => toggleGisModal(false)}
      size="large"
      className="containerReducedWhiteSpace"
      closeIcon
    >
      <Modal.Header>{t('gis.modal.precise.location.title')}</Modal.Header>
      <Modal.Content>
        <Loading isLoading={!isLoaded && !!(window as any).CERNMap} />
        <div id="map">
          {!(window as any).CERNMap && <Message color="yellow">{t('map.loading.error')}</Message>}
        </div>
      </Modal.Content>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  ...state.gisModal,
});

const mapDispatchToProps = (dispatch: any) => ({
  toggleGisModal: (state: boolean) => dispatch(gisModalActions.toggle(state)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GisModal);
